import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { EmojiPicker as FrimousseEmojiPicker } from 'frimousse';
import './BayEmojiPicker.scss';

interface BayEmojiPickerProps {
	// Controlled mode
	isOpen?: boolean;
	onToggle?: (_isOpen: boolean) => void;

	// Emoji selection callback
	onEmojiSelect: (_emoji: string) => void;

	// Trigger element (render prop or element)
	children: React.ReactNode;

	// Positioning
	placement?: 'top' | 'bottom' | 'left' | 'right';

	// Styling
	className?: string;
	containerClassName?: string;
	width?: number;
	height?: number;

	// Customization
	searchPlaceholder?: string;
	showSearch?: boolean;

	// Translation
	t?: (_key: string, _defaultValue?: string) => string;
}

// Emoji categories for navigation
const EMOJI_CATEGORIES = [
	{ id: 'smileys-emotion', emoji: '😀', label: 'Smileys & Emotion' },
	{ id: 'people-body', emoji: '👋', label: 'People & Body' },
	{ id: 'animals-nature', emoji: '🐶', label: 'Animals & Nature' },
	{ id: 'food-drink', emoji: '🍕', label: 'Food & Drink' },
	{ id: 'travel-places', emoji: '✈️', label: 'Travel & Places' },
	{ id: 'activities', emoji: '⚽', label: 'Activities' },
	{ id: 'objects', emoji: '💡', label: 'Objects' },
	{ id: 'symbols', emoji: '❤️', label: 'Symbols' },
	{ id: 'flags', emoji: '🏳️', label: 'Flags' },
];

const LIST_BOTTOM_PADDING_STYLE = {
	paddingBottom:
		'calc(var(--frimousse-viewport-height, 0px) - var(--frimousse-category-header-height, 0px))',
} as const;

export function BayEmojiPicker({
	isOpen: controlledIsOpen,
	onToggle,
	onEmojiSelect,
	children,
	placement = 'bottom',
	className = '',
	containerClassName = '',
	width = 352,
	height = 420,
	searchPlaceholder,
	showSearch = true,
	t = (key: string, defaultValue?: string) => defaultValue || key,
}: BayEmojiPickerProps) {
	// Internal state for uncontrolled mode
	const [internalIsOpen, setInternalIsOpen] = useState(false);

	// Determine if we're in controlled mode
	const isControlled = controlledIsOpen !== undefined;
	const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

	// Refs for trigger element and viewport
	const targetRef = useRef<HTMLDivElement>(null);
	const viewportRef = useRef<HTMLDivElement>(null);

	// Track active category
	const [activeCategory, setActiveCategory] = useState<string>(
		EMOJI_CATEGORIES[0].id,
	);

	// Handle toggle
	const handleToggle = useCallback(() => {
		const newState = !isOpen;

		if (isControlled && onToggle) {
			onToggle(newState);
		} else {
			setInternalIsOpen(newState);
		}
	}, [isOpen, isControlled, onToggle]);

	// Handle close
	const handleClose = useCallback(() => {
		if (isControlled && onToggle) {
			onToggle(false);
		} else {
			setInternalIsOpen(false);
		}
	}, [isControlled, onToggle]);

	// Handle emoji selection — keep picker open so user can insert multiple emojis
	const handleEmojiSelect = useCallback(
		(data: { emoji: string }) => {
			onEmojiSelect(data.emoji);
		},
		[onEmojiSelect],
	);

	// Memoized SkinTone render prop children — prevents new function instances on
	// every render, which would otherwise cause frimousse to do unnecessary work
	// and could interfere with search-input focus. Each skin-tone button also uses
	// onMouseDown preventDefault so that clicking a dot does not steal keyboard
	// focus away from the search input.
	const renderSkinToneChildren = useCallback(
		({
			skinTone: activeSkinTone,
			setSkinTone,
			skinToneVariations,
		}: {
			skinTone: string;
			setSkinTone: (_skinTone: string) => void;
			skinToneVariations: Array<{ skinTone: string; emoji: string }>;
		}) => (
			<div className="d-flex gap-1 flex-shrink-0">
				{skinToneVariations.map((variation) => (
					// Skin tone colors come from CSS variables so themes can override them.
					<button
						key={variation.skinTone}
						type="button"
						className={`bay-emoji-picker-skin-dot${
							activeSkinTone === variation.skinTone ? ' active' : ''
						}`}
						style={{
							backgroundColor: `var(--bay-emoji-skin-tone-${variation.skinTone}, var(--bay-emoji-skin-tone-none))`,
						}}
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => setSkinTone(variation.skinTone)}
						title={
							variation.skinTone === 'none'
								? 'Default skin tone'
								: variation.skinTone.replace(/-/g, ' ')
						}
						aria-label={`Skin tone: ${
							variation.skinTone === 'none' ? 'default' : variation.skinTone
						}`}
					/>
				))}
			</div>
		),
		[],
	);

	// Handle category click - scroll to category header
	const handleCategoryClick = useCallback((categoryId: string) => {
		const viewport = viewportRef.current;
		if (!viewport) return;

		setActiveCategory(categoryId);

		// Category headers are always in the DOM; frimousse uses CSS absolute
		// positioning, not DOM virtualisation. Each header lives inside a
		// [frimousse-category] wrapper whose `top` inline style is a calc()
		// expression. getComputedStyle resolves it to the exact scrollTop value.
		const categoryHeader = viewport.querySelector(
			`[data-category="${categoryId}"]`,
		);
		if (!categoryHeader) return;

		const wrapper = categoryHeader.closest(
			'[frimousse-category]',
		) as HTMLElement | null;
		if (wrapper) {
			const top = parseFloat(getComputedStyle(wrapper).top);
			if (!isNaN(top)) {
				viewport.scrollTo({ top, behavior: 'smooth' });
				return;
			}
		}

		// Fallback: best-effort positional scroll
		const viewportRect = viewport.getBoundingClientRect();
		const headerRect = (categoryHeader as HTMLElement).getBoundingClientRect();
		viewport.scrollTo({
			top: viewport.scrollTop + headerRect.top - viewportRect.top,
			behavior: 'smooth',
		});
	}, []);

	// Track scroll position to update active category
	useEffect(() => {
		if (!isOpen || !viewportRef.current) return;

		const viewport = viewportRef.current;
		let scrollTimeout: ReturnType<typeof setTimeout>;

		const handleScroll = () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				// Find the last category whose section starts at or before the
				// current scrollTop — that is the category currently at the top.
				// Using the [frimousse-category] wrapper's computed top (which
				// resolves the calc() expression) is reliable for sticky headers.
				const scrollTop = viewport.scrollTop;
				let lastVisible: string | null = null;
				const categoryHeaders = viewport.querySelectorAll('[data-category]');
				for (const header of Array.from(categoryHeaders)) {
					const wrapper = (header as HTMLElement).closest(
						'[frimousse-category]',
					) as HTMLElement | null;
					if (!wrapper) continue;
					const wrapperTop = parseFloat(getComputedStyle(wrapper).top);
					if (!isNaN(wrapperTop) && wrapperTop <= scrollTop + 30) {
						lastVisible = (header as HTMLElement).dataset.category ?? null;
					}
				}
				if (lastVisible) setActiveCategory(lastVisible);
			}, 100);
		};

		viewport.addEventListener('scroll', handleScroll);
		return () => {
			viewport.removeEventListener('scroll', handleScroll);
			clearTimeout(scrollTimeout);
		};
	}, [isOpen]);

	return (
		<>
			<div
				ref={targetRef}
				onClick={handleToggle}
				className={className}
				style={{ display: 'inline-block', cursor: 'pointer' }}
			>
				{children}
			</div>

			<Overlay
				show={isOpen}
				target={targetRef.current}
				placement={placement}
				rootClose
				onHide={handleClose}
			>
				<Popover className="bay-emoji-picker bay-emoji-picker-popover shadow">
					<Popover.Body className="p-0">
						<FrimousseEmojiPicker.Root onEmojiSelect={handleEmojiSelect}>
							<div
								className={`d-flex flex-column bay-emoji-picker-container bg-white ${containerClassName}`}
								style={{ width: `${width}px`, height: `${height}px` }}
							>
								{/* Category Navigation */}
								<div className="bay-emoji-picker-categories border-bottom p-2">
									<div className="d-flex gap-1 justify-content-around flex-wrap">
										{EMOJI_CATEGORIES.map((category) => (
											<button
												key={category.id}
												type="button"
												className={`bay-emoji-picker-category-button ${
													activeCategory === category.id ? 'active' : ''
												}`}
												title={category.label}
												onClick={() => handleCategoryClick(category.id)}
												aria-label={category.label}
											>
												{category.emoji}
											</button>
										))}
									</div>
								</div>

								{/* Search + skin-tone dots on the same row — mirrors PHP picker layout */}
								<div className="bay-emoji-picker-search-row d-flex align-items-center gap-2 px-3 pt-2 pb-2 border-bottom">
									{showSearch && (
										<FrimousseEmojiPicker.Search
											className="form-control form-control-sm bay-emoji-picker-search flex-grow-1"
											placeholder={
												searchPlaceholder ||
												t('emoji_picker.search', 'Search emoji...')
											}
										/>
									)}
									<FrimousseEmojiPicker.SkinTone emoji="🤚">
										{renderSkinToneChildren}
									</FrimousseEmojiPicker.SkinTone>
								</div>

								<FrimousseEmojiPicker.Viewport
									ref={viewportRef}
									className="position-relative flex-fill overflow-auto bay-emoji-picker-viewport"
								>
									<FrimousseEmojiPicker.Loading className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center text-muted small">
										{t('emoji_picker.loading', 'Loading...')}
									</FrimousseEmojiPicker.Loading>

									<FrimousseEmojiPicker.Empty className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center text-muted small">
										{t('emoji_picker.no_results', 'No emoji found.')}
									</FrimousseEmojiPicker.Empty>

									<FrimousseEmojiPicker.List
										className="user-select-none"
										style={LIST_BOTTOM_PADDING_STYLE}
										components={{
											CategoryHeader: ({ category, ...props }) => {
												const categoryId = category.label
													.toLowerCase()
													.replace(/[^a-z0-9]+/g, '-')
													.replace(/^-|-$/g, '');
												return (
													<div
														className="bay-emoji-picker-category-header"
														data-category={categoryId}
														{...props}
													>
														{category.label}
													</div>
												);
											},
											// Pass frimousse's props through unchanged so the
											// measured row/emoji heights (used for absolute
											// positioning) are not distorted by extra margins
											// or hard-coded dimensions.
											Row: ({ children: rowChildren, ...props }) => (
												<div {...props}>{rowChildren}</div>
											),
											Emoji: ({ emoji, ...props }) => (
												<button
													type="button"
													className="bay-emoji-picker-emoji-button"
													{...props}
												>
													{emoji.emoji}
												</button>
											),
										}}
									/>
								</FrimousseEmojiPicker.Viewport>
							</div>
						</FrimousseEmojiPicker.Root>
					</Popover.Body>
				</Popover>
			</Overlay>
		</>
	);
}
